/**
 * A contact's message history, on their profile.
 *
 * The opt-out toggle lives here rather than in the details panel because this
 * is where somebody is thinking about messaging them — and because a consent
 * switch belongs next to the evidence of what has been sent.
 */
import { useMutation, useQuery } from '@apollo/client';
import { Badge, Button, Card, EmptyState, Spinner } from '@meshhub/design-tokens/ui';
import { Send } from 'lucide-react';
import React, { useState } from 'react';
import { ConfirmDialog, MutationError } from '../FormKit.web.jsx';
import { SendMessageDialog } from '../messaging/SendMessageDialog.web.jsx';
import { formatDateTime } from '../../lib/format.js';
import { MESSAGES_QUERY, SET_MESSAGING_OPT_OUT } from '../../lib/queries.js';
import { MESSAGE_STATUS_LABELS, SUPPRESSION_LABELS } from '../../screens/messaging/Outbox.web.jsx';
import { S } from '../../lib/strings.js';

export function MessagesPanel({ contact, onChanged }) {
  const [sending, setSending] = useState(false);
  const [confirmOptOut, setConfirmOptOut] = useState(false);

  const { data, loading, refetch } = useQuery(MESSAGES_QUERY, {
    variables: { page: 1, pageSize: 20, contactId: contact.id },
    errorPolicy: 'ignore',
  });
  const [setOptOut, optOutState] = useMutation(SET_MESSAGING_OPT_OUT, {
    onCompleted: () => onChanged?.(),
  });

  const messages = data?.messages?.items ?? [];

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <h2 className="text-md font-bold">{S.messaging.title}</h2>
        <Button variant="primary" onClick={() => setSending(true)}>
          <Send size={13} aria-hidden="true" />
          {S.messaging.send}
        </Button>
        <label className="ms-auto flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="accent-primary"
            checked={contact.messagingOptOut}
            onChange={() => setConfirmOptOut(true)}
          />
          {S.search.optedOut}
        </label>
      </div>

      <MutationError error={optOutState.error} />
      {loading && messages.length === 0 && <Spinner />}
      {!loading && messages.length === 0 && <EmptyState title={S.messaging.noMessages} />}

      <ul className="flex flex-col gap-2">
        {messages.map((message) => (
          <li key={message.id} className="border-b border-border pb-2 last:border-b-0">
            <p className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
              <span>{MESSAGE_STATUS_LABELS[message.status]}</span>
              {message.suppressionReason && (
                <Badge className="bg-surface-hover text-warning">
                  {SUPPRESSION_LABELS[message.suppressionReason]}
                </Badge>
              )}
              <span>
                {message.sentAt ? formatDateTime(message.sentAt) : formatDateTime(message.createdAt)}
              </span>
            </p>
            <p className="whitespace-pre-wrap text-sm">{message.renderedBody}</p>
          </li>
        ))}
      </ul>

      <SendMessageDialog
        open={sending}
        onOpenChange={setSending}
        contact={contact}
        onSent={() => refetch()}
      />
      <ConfirmDialog
        open={confirmOptOut}
        onOpenChange={setConfirmOptOut}
        title={S.search.optedOut}
        body={S.messaging.forceHint}
        confirmLabel={S.app.confirm}
        onConfirm={async () => {
          await setOptOut({
            variables: { contactId: contact.id, optedOut: !contact.messagingOptOut },
          });
          setConfirmOptOut(false);
        }}
      />
    </Card>
  );
}
